<template>
<div class="flex justify-center items-center">
  <form
    class="w-full"
    @submit.prevent="login()">
    <input type="submit" class="hidden"/>

    <vs-col class="p-1 sm:p-2 md:p-4">
      <vs-input
        v-model="email"
        class="w-full required mt-0"
        v-validate="'required|email|min:5|max:60'"
        data-vv-validate-on="input"
        :name="$tc('$General.Email')"
        autocomplete="email"
        icon-no-border
        icon="icon icon-mail"
        icon-pack="feather"
        :label="$tc('$General.Email')"
        :danger="emailHasErrors"
        :danger-text="emailErrorMsg"
        val-icon-danger="clear"
        @input="wrongEmailError = false"/>

      <password-input
        v-model="password"
        class="w-full mt-10"
        data-vv-validate-on="input"
        v-validate="'required'"
        :label="$t('$General.Password')"
        :name="$t('$General.Password')"
        autocomplete="current-password"
        :danger="passwordHasErrors"
        :danger-text="passwordErrorMsg"
        val-icon-danger="clear"
        required
        @input="wrongPasswordError = false"/>

      <forgot-password-link @click="activeForgotPasswordModal = true"/>

      <div class="w-full mt-5">
        <vs-button
          class="w-full"
          @click="login()" >{{ $t('LoginWithEmail') }}</vs-button>
      </div>

      <vs-divider class="my-6">{{ $t('$General.Or') | uppercase }}</vs-divider>

      <sign-in-google-button
        :text="$t('LoginWithGoogle')"
        @click="redirectLoginGoogle">
      </sign-in-google-button>
    </vs-col>
  </form>

  <send-forgot-password-email-popup
    v-model="activeForgotPasswordModal"
  />
</div>
</template>

<script>
import {mapActions, mapGetters, mapMutations, mapState} from 'vuex';
import configApi from '@/api/config';

// Components
import SignInGoogleButton from '@/views/pages/_components/GoogleButton.vue';
import ForgotPasswordLink from '@/views/modules/_components/ForgotPasswordLink.vue';
import SendForgotPasswordEmailPopup from '@/views/pages/auth/components/SendForgotPasswordEmailPopup.vue';
import PasswordInput from '@/components/PasswordInput.vue';

/**
 * Component to login on the app
 *
 * @module views/pages/auth/TheAuthLogin
 * @author Dilan Useche <dilan8810@gmail.com>
 *
 */
export default {
  name: 'TheAuthLogin',
  i18n: {
    messages: {
      en: {
        LoginWithEmail: 'Log in with email',
        LoginWithGoogle: 'Log in with Google',
        GoogleLoginErrorNotifyTitle: 'Google sign-up',
        GoogleLoginErrorNotifyMsg: 'An error has occurred with the verification of your Google account',
      },
    },
  },
  components: {
    SignInGoogleButton,
    ForgotPasswordLink,
    SendForgotPasswordEmailPopup,
    PasswordInput,
  },
  data() {
    return {
      email: '',
      password: '',

      wrongEmailError: false,
      wrongPasswordError: false,
      WrongEmailOrPasswordErrorMsg: '',
      activeForgotPasswordModal: false,
    };
  },
  computed: {
    ...mapState({
      redirectAfterLogin: (state) => state.redirectAfterLogin,
    }),
    ...mapGetters({
      getUserFlag: 'auth/getUserFlag',
    }),
    emailHasErrors() {
      return this.errors.has(this.$tc('$General.Email')) || this.wrongEmailError;
    },
    emailErrorMsg() {
      return this.errors.first(this.$tc('$General.Email')) || this.WrongEmailOrPasswordErrorMsg;
    },
    passwordHasErrors() {
      return this.errors.has(this.$t('$General.Password')) || this.wrongPasswordError;
    },
    passwordErrorMsg() {
      return this.errors.first(this.$t('$General.Password')) || this.WrongEmailOrPasswordErrorMsg;
    },
  },
  created() {
    this.verifyQueryParamToken();
  },
  methods: {
    ...mapActions({
      signIn: 'auth/login',
      verifyToken: 'auth/verifyToken',
    }),
    ...mapMutations({
      REMOVE_REDIRECT_AFTER_LOGIN: 'REMOVE_REDIRECT_AFTER_LOGIN',
    }),
    redirectLoginGoogle() {
      window.location.href = `${configApi.url}/auth/google/false/false`;
    },
    async verifyQueryParamToken() {
      if (this.$route.query.token && this.$route.query.refreshToken) {
        this.$vs.loading({ type: 'radius' });
        await this.verifyToken({
          token: this.$route.query.token,
          refreshToken: this.$route.query.refreshToken,
        });
        if (this.redirectAfterLogin) {
          await this.$router.push({ name: this.redirectAfterLogin }).catch(() => {});
          this.REMOVE_REDIRECT_AFTER_LOGIN();
        } else {
          await this.$router.push({ name: 'home' }).catch(() => {});
        }

        this.$vs.loading.close();

        if (this.getUserFlag('acceptTermsAndConditions')) {
          this.$showSuccessActionNotification({
            title: this.$t('$Auth.LoginAttempt'),
            text: this.$t('$Auth.SuccessfullyLoginMsg'),
          });
        }
      } else if (this.$route.query.authEmailVerificationSuccess) {
        this.$showSuccessActionNotification({
          title: this.$t('$Auth.$EmailAccountVerification.VerificationNotificationTitle'),
          text: this.$t('$Auth.$EmailAccountVerification.VerificationNotificationOkMsg'),
        });
      } else if (this.$route.query.authEmailVerificationError) {
        this.$emit('email-unverified-login-attempt', this.$route.query.email);

        this.$showGeneralErrorNotification({
          title: this.$t('$Auth.$EmailAccountVerification.VerificationNotificationTitle'),
          text: this.$t('$Auth.$EmailAccountVerification.InvalidLinkClickedErrorMsg'),
        });
      } else if (this.$route.query.authGoogleError) {
        this.$showGeneralErrorNotification({
          title: this.$t('GoogleLoginErrorNotifyTitle'),
          text: this.$t('GoogleLoginErrorNotifyMsg'),
        });
      }
    },
    async login() {
      const formIsValid = await this.$validator.validate();

      if (!formIsValid) {
        document.getElementsByName([this.$validator.errors.items[0].field])[0].focus();
        return;
      }

      this.$vs.loading({ type: 'radius' });

      try {
        const { data } = await this.signIn({
          email: this.email,
          password: this.password,
        });

        if (data.twoFactorAuthenticationActivate) {
          await this.$router.push({
            name: 'login-second-step',
            query: { tokenSecondStep: data.tokenSecondStep },
          }).catch(() => {});
          this.$vs.loading.close();
        } else {
          if (this.redirectAfterLogin) {
            await this.$router.push({ name: this.redirectAfterLogin }).catch(() => {});
            this.REMOVE_REDIRECT_AFTER_LOGIN();
          } else {
            await this.$router.push({ name: 'home' }).catch(() => {});
          }

          this.$vs.loading.close();

          this.$showSuccessActionNotification({
            title: this.$t('$Auth.LoginAttempt'),
            text: this.$t('$Auth.SuccessfullyLoginMsg'),
          });
        }
      } catch (e) {
        console.log('Error en login', e.status, e.response);
        this.$vs.loading.close();

        if (e.response && e.response.data) {
          if (e.response.data.emailStatus === this.$enums.Users.Status.EMAIL_NOT_VERIFIED) {
            this.$emit('email-unverified-login-attempt', this.email);
          } else if (e.response.data.wrongEmail || e.response.data.wrongPassword) {
            this.wrongEmailError = e.response.data.wrongEmail || false;
            this.wrongPasswordError = e.response.data.wrongPassword || false;
            this.WrongEmailOrPasswordErrorMsg = e.response.data.message;
          } else {
            throw e;
          }
        } else {
          throw e;
        }
      }
    },
  },
};
</script>
